import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppNav } from "@/components/AppNav";
import { useState } from "react";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { supabase, API_URL } from "@/lib/supabase";
import { posthog } from "@/lib/posthog";

const REASONS = [
  { value: "found_car_here",      label: "Encontrei meu carro pela Busca Blindados 🎉" },
  { value: "found_car_elsewhere", label: "Encontrei meu carro por outro caminho" },
  { value: "too_expensive",       label: "Muito caro para o que oferece" },
  { value: "no_value",            label: "Não encontrei valor suficiente" },
  { value: "other",               label: "Outro motivo" },
] as const;

export const Route = createFileRoute("/cancelar")({
  head: () => ({
    meta: [{ name: "robots", content: "noindex" }],
  }),
  component: CancelarPage,
});

function CancelarPage() {
  const { session, loading: sessionLoading } = useSession();
  const { isPaid, profile, loading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [planExpiresAt, setPlanExpiresAt] = useState<string | null>(null);

  if (!sessionLoading && !session) {
    navigate({ to: "/login" });
    return null;
  }
  if (!profileLoading && !isPaid) {
    navigate({ to: "/minha-conta" });
    return null;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) {
      setError("Selecione um motivo para continuar.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: { session: fresh } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/cancel-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fresh?.access_token ?? session!.access_token}`,
        },
        body: JSON.stringify({ reason, feedback }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Algo deu errado. Tente novamente.");
        setLoading(false);
        return;
      }
      posthog.capture("plan_cancelled", { reason, plan_id: profile?.plan_id });
      setPlanExpiresAt(data.plan_expires_at ?? null);
      setCancelled(true);
    } catch {
      setError("Falha de conexão. Tente novamente.");
      setLoading(false);
    }
  };

  if (cancelled) {
    const expiryDate = (planExpiresAt ?? profile?.plan_expires_at)
      ? new Date(planExpiresAt ?? profile!.plan_expires_at!).toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : null;

    return (
      <PageShell>
        <p className="font-display text-2xl text-foreground">Plano cancelado.</p>
        <p className="mt-3 text-sm text-muted-foreground">
          {expiryDate
            ? `Seu acesso continua até ${expiryDate}.`
            : "Seu acesso foi encerrado."}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Obrigado pelo feedback — nos ajuda muito a melhorar.
        </p>
        <Link
          to="/"
          className="mt-8 inline-block text-sm text-primary hover:underline"
        >
          Voltar ao início
        </Link>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <h1 className="font-display text-2xl text-foreground">Cancelar plano</h1>
      <p className="mt-2 mb-6 text-sm text-muted-foreground">
        Antes de ir, conta pra gente o que aconteceu?
      </p>

      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          {REASONS.map((r) => (
            <label
              key={r.value}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition ${
                reason === r.value
                  ? "border-primary/40 bg-primary/10 text-foreground"
                  : "dark:border-white/10 border-black/10 text-muted-foreground dark:hover:border-white/20 hover:border-black/20 hover:text-foreground"
              }`}
            >
              <input
                type="radio"
                name="reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => { setReason(r.value); setError(null); }}
                className="sr-only"
              />
              {r.label}
            </label>
          ))}
        </div>

        <div>
          <label className="mb-1.5 block text-xs text-muted-foreground">
            Algo mais que queira compartilhar? (opcional)
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            placeholder="Sua opinião nos ajuda a melhorar..."
            className="w-full resize-none rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary/60"
          />
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex flex-col gap-2 pt-2">
          <button
            type="submit"
            disabled={loading || !reason}
            className="w-full rounded-full border border-red-500/30 bg-red-500/10 py-3 text-sm font-medium text-red-400 transition hover:bg-red-500/20 disabled:opacity-40"
          >
            {loading ? "Cancelando…" : "Confirmar cancelamento"}
          </button>
          <Link
            to="/minha-conta"
            className="block w-full rounded-full border dark:border-white/10 border-black/10 py-3 text-center text-sm text-muted-foreground transition hover:text-foreground"
          >
            Manter meu plano
          </Link>
        </div>
      </form>
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen text-foreground">
      <AppNav maxWidth="max-w-5xl" />
      <main className="mx-auto max-w-md px-6 pb-32 pt-10">
        <div className="surface-card rounded-3xl px-8 py-10">{children}</div>
      </main>
    </div>
  );
}
